/**
 * MetadataFixRow — one editable metadata field in the FIX section.
 *
 * Shows the field label, an inline input/textarea prefilled with the current
 * value, the violation messages in plain language beneath, and the band meter
 * (the signature element) for fields with a target range. Slug rows get a
 * validity chip instead of a band.
 *
 * Rows re-evaluate live as the reviewer types — a fixed row visibly turns from
 * coral to teal and its violation text disappears. No toast, no confetti.
 *
 * Each violation message is followed by a short, honest fix hint (from the
 * RULE_HINTS map) that tells the reviewer WHAT to do — never an invented
 * corrected value. Unknown ruleIds get no hint (better silent than wrong).
 */

import React from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import type { MetadataViolation } from '@/lib/metadataCriteria';
import { BandMeter, BAND_RANGES } from './BandMeter';
import { AiSuggestButton } from '@/components/editor-chrome/rail/cards/AiSuggestButton';

const FIX_FIELD_LABELS: Record<string, string> = {
  meta_title: 'Meta title',
  meta_description: 'Meta description',
  title: 'Title (H1)',
  url_slug: 'URL slug',
  og_title: 'OG title',
  og_description: 'OG description',
  twitter_title: 'Twitter title',
  twitter_description: 'Twitter description',
  excerpt: 'Excerpt',
};

const TEXTAREA_FIELDS = new Set([
  'meta_description',
  'og_description',
  'twitter_description',
  'excerpt',
]);

/**
 * Fields that have an owning postgen generator and can get a Suggest button.
 * Widened to the full backend SUGGESTIBLE_FIELDS set so every eligible row
 * gets the "apply suggested value" action.
 *
 * Backend (metadata_suggest.py SUGGESTIBLE_FIELDS):
 *   title, meta_title, meta_description, og_title, og_description,
 *   twitter_title, twitter_description, excerpt
 *
 * url_slug is intentionally excluded — the backend does not support slug
 * suggestions (and a slug is not generated by postgen).
 */
const SUGGESTIBLE_FIX_FIELDS = new Set([
  'meta_title',
  'meta_description',
  'og_title',
  'og_description',
  'twitter_title',
  'twitter_description',
  'excerpt',
  'title',
]);

// ---------------------------------------------------------------------------
// Fix-hint copy — ruleId → short directional hint.
//
// Honest by design: the hint describes WHAT action to take, never an invented
// corrected value (no "change to 'Best Service | Brand'", no fake scores).
// Unknown ruleIds fall back to undefined (no hint) — better silent than wrong.
// ---------------------------------------------------------------------------

const RULE_HINTS: Record<string, string> = {
  // ── meta_title ──────────────────────────────────────────────────────
  meta_title_length: 'Aim for 50–60 characters so the full title shows in SERP.',
  meta_title_pipe: 'Add a "|" between the keyword phrase and brand name.',
  meta_title_keyword: 'Weave the target keyword into the title naturally.',
  meta_title_brand: 'Append the brand after the pipe, e.g. "… | Brand".',
  meta_title_forbidden_chars: 'Remove any of these characters: [ ] { } " `',
  meta_title_artifact: 'Strip dates, UUIDs, or run/draft markers from the title.',
  meta_title_incomplete_phrase: 'Rewrite so the title ends on a complete phrase, not a CTA fragment.',
  meta_title_stray_dash: 'Replace " - " with a single "|" pipe.',
  meta_title_keyword_frontload: 'Move the keyword into the first half of the title.',
  meta_title_present: 'Add a meta title — it must carry to WordPress.',

  // ── H1 / title ──────────────────────────────────────────────────────
  h1_title_case: 'Use Title Case for the H1.',
  h1_pipe: 'Remove the "|" pipe — pipes belong in the meta title only.',
  h1_dash: 'Remove the " - " dash separator from the H1.',
  h1_brand: 'Remove the brand name from the H1.',
  h1_equals_keyword: 'Rewrite as a natural headline, not the raw keyword.',
  h1_location_suffix: 'Add real headline structure beyond "keyword near me".',
  h1_empty: 'Add an on-page H1 headline.',
  h1_meta_alignment: 'Anchor both the H1 and meta title on the same primary keyword.',

  // ── meta_description ────────────────────────────────────────────────
  meta_description_length: 'Aim for 140–155 characters for full SERP display.',
  meta_description_complete: 'End with a complete sentence (period or full stop).',
  meta_description_brand: 'Mention the brand name once in the description.',
  meta_description_keyword: 'Include the target keyword in the description.',
  meta_description_url: 'Remove any URLs from the description.',
  meta_description_markdown: 'Remove markdown syntax — the description is plain text.',
  meta_description_duplicate_sentence: 'Rewrite the repeated sentence so it adds new info.',
  meta_description_present: 'Add a meta description — it must carry to WordPress.',

  // ── og_title ────────────────────────────────────────────────────────
  og_title_length: 'Shorten the OG title to fit the Open Graph card.',
  og_title_url: 'OG title must not contain a URL or domain.',
  og_title_markdown: 'OG title must not contain markdown syntax.',
  og_title_forbidden_chars: 'Remove any of these characters: [ ] { } " `',
  og_title_artifact: 'Strip dates, UUIDs, or run/draft markers from the OG title.',

  // ── og_description ──────────────────────────────────────────────────
  og_description_length: 'Aim for the OG description target range.',
  og_description_complete: 'End the OG description with a complete sentence.',
  og_description_brand: 'Mention the brand name in the OG description.',
  og_description_rating_leak: 'Remove any rating-widget or review-count leaks.',
  og_description_url: 'OG description must not contain a URL.',
  og_description_markdown: 'OG description must not contain markdown.',
  og_description_forbidden_chars: 'Remove any of these characters: [ ] { } " `',
  og_description_artifact: 'Strip dates, UUIDs, or run/draft markers.',

  // ── twitter_title ───────────────────────────────────────────────────
  twitter_title_length: 'Shorten the Twitter title to fit the card.',
  twitter_title_url: 'Twitter title must not contain a URL.',
  twitter_title_markdown: 'Twitter title must not contain markdown.',
  twitter_title_forbidden_chars: 'Remove any of these characters: [ ] { } " `',
  twitter_title_artifact: 'Strip dates, UUIDs, or run/draft markers.',

  // ── twitter_description ─────────────────────────────────────────────
  twitter_description_length: 'Shorten the Twitter description to fit the card.',
  twitter_description_complete: 'End the Twitter description with a complete sentence.',
  twitter_description_url: 'Twitter description must not contain a URL.',
  twitter_description_markdown: 'Twitter description must not contain markdown.',
  twitter_description_forbidden_chars: 'Remove any of these characters: [ ] { } " `',
  twitter_description_artifact: 'Strip dates, UUIDs, or run/draft markers.',

  // ── url_slug ────────────────────────────────────────────────────────
  url_slug_artifact: 'Clean the slug: drop ?p=ID, -qa-date, -YYYYMMDD, or trailing -N.',
  slug_collision: 'Edit the slug, or confirm the collision is acceptable (WordPress will append "-2").',

  // ── focus_keyword ───────────────────────────────────────────────────
  focus_keyword: 'Restore the full target keyword in the focus keyword field.',

  // ── pipeline / human / provenance / accessibility ───────────────────
  backend_status_failed: 'Open the article and resolve the backend failure, or accept the risk to publish.',
  human_qa_pending: 'Complete human QA sign-off, or accept the risk to publish.',
  provenance_fallback: 'Regenerate metadata for this field, or edit it manually.',
  image_missing_alt: 'Add descriptive alt text to the image, or mark it decorative.',
};

/** Prefix-based fallbacks for parameterized ruleIds (e.g. human_verify_*). */
const RULE_HINT_PREFIXES: Array<[string, string]> = [
  ['human_verify_', 'Confirm or flag this item — see details for evidence.'],
];

/**
 * Look up a short, honest fix hint for a ruleId. Returns undefined when no
 * hint is available — the caller should simply omit the hint line.
 */
export function getRuleHint(ruleId: string): string | undefined {
  const exact = RULE_HINTS[ruleId];
  if (exact) return exact;
  for (const [prefix, hint] of RULE_HINT_PREFIXES) {
    if (ruleId.startsWith(prefix)) return hint;
  }
  return undefined;
}

export interface MetadataFixRowProps {
  field: string;
  value: string;
  violations: MetadataViolation[];
  onChange: (value: string) => void;
  /** Content ID for the per-field suggest endpoint. */
  contentId?: string;
  /** Tenant ID for the per-field suggest endpoint. */
  tenantId?: string;
  /** Disable Suggest while a whole-run metadata generation is in flight. */
  suggestDisabled?: boolean;
}

export const MetadataFixRow: React.FC<MetadataFixRowProps> = ({
  field,
  value,
  violations,
  onChange,
  contentId,
  tenantId,
  suggestDisabled = false,
}) => {
  const label = FIX_FIELD_LABELS[field] ?? field;
  const isTextarea = TEXTAREA_FIELDS.has(field);
  const band = BAND_RANGES[field];
  const hasViolations = violations.length > 0;

  const inputClasses =
    'mt-1 block w-full rounded-md border px-3 py-2 text-sm text-foreground outline-none motion-safe:transition-colors focus:border-teal';
  const borderClass = hasViolations
    ? 'border-coral/40 focus:border-coral'
    : 'border-teal/30';

  const inputId = `fix-field-${field}`;

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-foreground"
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          {contentId && tenantId && SUGGESTIBLE_FIX_FIELDS.has(field) && (
            <AiSuggestButton
              field={field}
              onSuggest={onChange}
              currentValue={value}
              contentId={contentId}
              tenantId={tenantId}
              disabled={suggestDisabled}
            />
          )}
          {/* Slug validity chip / status indicator */}
          {field === 'url_slug' && (
          <span
            className={
              hasViolations
                ? 'inline-flex items-center gap-1 rounded-full bg-coral-light px-2 py-0.5 text-xs font-medium text-coral-dark'
                : 'inline-flex items-center gap-1 rounded-full bg-teal-light px-2 py-0.5 text-xs font-medium text-teal-dark'
            }
          >
            {hasViolations ? (
              <>
                <AlertTriangle size={11} className="shrink-0" />
                Artifact
              </>
            ) : (
              <>
                <Check size={11} className="shrink-0" />
                Clean
              </>
            )}
          </span>
        )}
        </div>
      </div>

      {isTextarea ? (
        <textarea
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className={`${inputClasses} ${borderClass} min-h-[44px] resize-y`}
        />
      ) : (
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClasses} ${borderClass}`}
        />
      )}

      {/* Band meter for fields with a target range */}
      {band && <div className="mt-1.5"><BandMeter current={value.length} range={band} /></div>}

      {/* Violation messages (live re-evaluated) + fix hints */}
      {hasViolations && (
        <ul className="mt-1.5 space-y-0.5">
          {violations.map((v) => {
            const hint = getRuleHint(v.ruleId);
            return (
              <li
                key={v.ruleId}
                className="flex items-start gap-1.5 text-xs text-coral-dark motion-safe:transition-colors"
              >
                <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                <span>
                  <span>{v.message}</span>
                  {hint && (
                    <span className="mt-0.5 block text-xs italic text-muted-foreground">
                      {hint}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MetadataFixRow;
